import { useEffect } from "react";

// Adds 'is-visible' to any element with class 'reveal' when it enters the viewport.
// Robust for: (1) Safari/WebKit (which can skip IntersectionObserver callbacks for
// elements already in view) and (2) content that loads asynchronously (Services,
// Menu, Gallery fetch their data after first render).
export default function useReveal() {
  useEffect(() => {
    const reveal = (el) => el.classList.add("is-visible");

    const inViewport = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh + 120 && r.bottom > -120;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    // Scan the DOM for not-yet-revealed elements. Reveal those already on screen
    // immediately (Safari-safe), observe the rest for scroll-in.
    const scan = () => {
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => {
        if (inViewport(el)) reveal(el);
        else observer.observe(el);
      });
    };

    scan();

    // Re-scan when new elements are added to the DOM (async data loads)
    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { childList: true, subtree: true });

    const onScroll = () => scan();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    // Safety net: after content settles, reveal anything already visible
    const t1 = setTimeout(scan, 800);
    const t2 = setTimeout(scan, 2000);

    return () => {
      observer.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);
}
